import {
  BarChartOutlined,
  DeploymentUnitOutlined,
  LogoutOutlined,
  SettingOutlined
} from '@ant-design/icons';
import {UserOutlined} from '@ant-design/icons/lib';
import {useApolloClient} from '@jimmyn/apollo-client';
import antd, {Avatar, Dropdown, Layout} from 'antd';
import {MenuProps} from 'antd/lib/menu';
import {ItemType} from 'antd/lib/menu/hooks/useItems';
import {AnchorLink, Spacer} from 'components';
import gravatar from 'gravatar';
import {useAccessLevel} from 'hooks/useAccessLevel';
// @ts-ignore
import {TestModeSwitch} from 'components/TestModeSwitch';
import initials from 'initials';
import {colors} from 'lib/colors';
import {UserContext} from 'lib/userContext';
import {MenuInfo} from 'rc-menu/lib/interface';
import React, {ReactNode, useContext} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {ApiExplorer} from 'routes/ApiExplorer';
import {config} from 'stage.config';
import styled from 'styled-components';
import {IAuthState} from 'types';
import {responsiveSizesMap} from '../lib/responsive';
interface IMenuProps extends MenuProps {
  children?: ReactNode;
}

interface IProps {
  minimal?: boolean;
}

const Header = styled(Layout.Header)`
  position: fixed;
  z-index: 10;
  width: 100%;
  @media (max-width: ${responsiveSizesMap.md}px) {
    padding: 0 !important;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  @media (max-width: ${responsiveSizesMap.md}px) {
    width: calc(100vw - 32px);
  }
  @media (max-width: ${responsiveSizesMap.sm}px) {
    li {
      padding: 0 12px !important;
    }
  }
`;

export const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
`;

const TestModeDesktop = styled.div`
  margin-right: 10px;
  min-width: 97px;
  @media (max-width: 1000px) {
    display: none;
  }
`;

const Menu = styled(antd.Menu)<IMenuProps>`
  && {
    line-height: 58px;
  }
`;

const Logo = styled.img`
  height: 30px;
  width: 139px;
  margin-bottom: 3px;
`;

export const MainHeader: React.FC<IProps> = ({minimal}) => {
  const {userEmail, userName, onSignOut} = useContext(UserContext);
  const client = useApolloClient();
  const location = useLocation();
  const {isAdmin, isMaster} = useAccessLevel();

  // Define an array of routes that we want to check for active selection
  const routes = ['/master-accounts', '/accounts', '/payments', '/affiliates'];

  // Determine the selected key based on the current location pathname
  const selectedKeys = [
    routes.find((route) => location.pathname.startsWith(route)) || location.pathname
  ];

  const signOut = async (state?: IAuthState) => {
    await onSignOut(state);
    await client.clearStore();
  };
  const handleUserMenuClick = async ({key}: MenuInfo) => {
    if (key === 'signOut') {
      await signOut();
      return;
    }
  };
  const handleMainMenuClick = ({key}: MenuInfo) => {};

  const minimalUserMenu = (
    <Menu
      onClick={handleUserMenuClick}
      items={[
        {
          key: 'signOut',
          title: 'Sign out',
          icon: <LogoutOutlined />
        }
      ]}></Menu>
  );

  const userMenuItems: ItemType[] = [
    ...(isAdmin
      ? [
          {
            key: '/users',
            label: (
              <Link to="/users">
                <UserOutlined /> Users
              </Link>
            )
          }
        ]
      : []),
    ...(isMaster
      ? [
          {
            key: '/settings',
            label: (
              <Link to="/settings">
                <SettingOutlined /> Settings
              </Link>
            )
          }
        ]
      : []),
    ...(isAdmin
      ? [
          {
            key: '/api-explorer',
            label: (
              <Link to="/api-explorer">
                <DeploymentUnitOutlined /> API Explorer
              </Link>
            )
          }
        ]
      : []),
    ...(isAdmin
      ? [
          {
            key: '/analytics',
            label: (
              <AnchorLink href={config.analyticsUrl} target="_blank">
                <BarChartOutlined /> Analytics
              </AnchorLink>
            )
          }
        ]
      : []),
    ...(isMaster
      ? [
          {
            type: 'divider'
          } as ItemType
        ]
      : []),
    {
      key: 'signOut',
      label: 'Sign out',
      icon: <LogoutOutlined />
    }
  ];

  const userMenu = (
    <Menu
      onClick={handleUserMenuClick}
      onMouseEnter={ApiExplorer.preload}
      selectedKeys={[location.pathname]}
      items={userMenuItems}></Menu>
  );

  const menuItems: ItemType[] = [
    {
      key: 'home',
      label: (
        <Link to="/">
          <Logo src="https://assets.monei.com/images/logo-white.svg" />
        </Link>
      )
    },
    ...(isMaster
      ? [
          {
            key: '/payments',
            label: <Link to="/payments?status=succeeded">Payments</Link>
          }
        ]
      : []),
    {
      key: '/accounts',
      label: <Link to="/accounts?status=ACTIVE">Accounts</Link>
    },
    ...(isAdmin
      ? [
          {
            key: '/master-accounts',
            label: <Link to="/master-accounts?status=ACTIVE">Master Accounts</Link>
          }
        ]
      : []),
    ...(isAdmin
      ? [
          {
            key: '/affiliates',
            label: <Link to="/affiliates">Affiliates</Link>
          }
        ]
      : [])
  ];

  return (
    <Header>
      <HeaderContent>
        <Menu
          theme="dark"
          mode="horizontal"
          onClick={handleMainMenuClick}
          disabledOverflow
          selectedKeys={selectedKeys}
          items={menuItems}></Menu>
        <Spacer />
        <TestModeDesktop>
          <TestModeSwitch />
        </TestModeDesktop>
        <Dropdown overlay={minimal ? minimalUserMenu : userMenu}>
          <Menu
            theme="dark"
            mode="horizontal"
            selectedKeys={[]}
            disabledOverflow
            items={[
              {
                key: '/user',
                label: (
                  <AnchorLink>
                    <Avatar
                      src={gravatar.url(userEmail, {default: '404'})}
                      style={{marginRight: 6, backgroundColor: colors.primary}}>
                      {initials(userEmail)}
                    </Avatar>{' '}
                    {userName || userEmail}
                  </AnchorLink>
                )
              }
            ]}
          />
        </Dropdown>
      </HeaderContent>
    </Header>
  );
};
